import { useParams, Link, useNavigate } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { ArrowLeft, Trash2, Settings } from 'lucide-react';
import { formatDateTime } from '../lib/utils';
import { toast } from 'sonner';

export function AppDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: app, isLoading } = useQuery({
    queryKey: ['app', id],
    queryFn: () => api.get('/api/apps/get', { params: { app_instance_id: id } }).then((r) => r.data),
    enabled: !!id,
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.post('/api/apps/delete_app', { app_instance_id: id }),
    onSuccess: () => {
      toast.success('App deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['apps'] });
      navigate('/apps');
    },
    onError: () => toast.error('Failed to delete app'),
  });

  if (isLoading) {
    return <div className="text-center py-12 text-gray-500">Loading app details...</div>;
  }

  if (!app) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 mb-4">App not found</p>
        <Link to="/apps"><Button variant="outline">Back to Apps</Button></Link>
      </div>
    );
  }

  const appData = app.query?.[0] ?? app;

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <Link to="/apps">
          <Button variant="ghost" size="icon"><ArrowLeft className="h-5 w-5" /></Button>
        </Link>
        <div className="flex items-center gap-3 flex-1">
          <img
            src={appData.app_logo || '/assets/images/default/organization.png'}
            alt={appData.app_name}
            className="w-12 h-12 rounded object-contain"
          />
          <div>
            <h1 className="text-2xl font-bold">{appData.app_name}</h1>
            <p className="text-gray-500">{appData.app_instance_name}</p>
          </div>
        </div>
        <Badge variant={appData.is_active ? 'success' : 'secondary'}>
          {appData.is_active ? 'Active' : 'Inactive'}
        </Badge>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg">App Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><span className="text-gray-500">App Name:</span> <span className="font-medium ml-2">{appData.app_name}</span></div>
            <div><span className="text-gray-500">Instance Name:</span> <span className="font-medium ml-2">{appData.app_instance_name}</span></div>
            <div><span className="text-gray-500">Created:</span> <span className="font-medium ml-2">{formatDateTime(appData.created)}</span></div>
            <div><span className="text-gray-500">Modified:</span> <span className="font-medium ml-2">{formatDateTime(appData.modified)}</span></div>
          </div>
        </CardContent>
      </Card>

      {/* Services for this app */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Settings className="h-5 w-5" /> Services
          </CardTitle>
          <CardDescription>Services running on this app instance</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-gray-500 text-sm">Manage services from the Services page.</p>
          <Link to="/services"><Button variant="outline" className="mt-3">View Services</Button></Link>
        </CardContent>
      </Card>

      <Separator className="my-6" />

      <div>
        <h2 className="text-lg font-semibold text-red-600 mb-3">Danger Zone</h2>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="destructive">
              <Trash2 className="h-4 w-4 mr-2" /> Delete App
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete {appData.app_instance_name}?</AlertDialogTitle>
              <AlertDialogDescription>
                This will soft-delete the app instance. This action can be undone by an administrator.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
                className="bg-red-600 hover:bg-red-700"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}